import { useState, useEffect, useMemo } from "react";
import { getPermissions, createPermission, deletePermission, updatePermission } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import { useNavigate } from "react-router-dom";
import FloatingInput from "../components/UI/FloatingInput";
import { notify } from "../utils/notify";
import ConfirmModal from "../components/UI/ConfirmModal";
import SearchInput from "../components/UI/SearchInput";
import DataTable from "../components/UI/DataTable"; // المكون المركزي
import PageHeader from "../components/UI/PageHeader";

export default function Permissions() {
  const [formData, setFormData] = useState({ id: null, name: "", description: "", module: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [sortConfig, setSortConfig] = useState({ key: "id", direction: "asc" });
  const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });
  const [status, setStatus] = useState('idle');
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchPermissions = async () => {
    try {
      const res = await getPermissions(token);
      setPermissions(res || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchPermissions(); }, []);
  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  const validateForm = () => {
    let newErrors = {};
    if (!formData.name) newErrors.name = "Permission name is required";
    if (!formData.module) newErrors.module = "Module is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      notify.error("Please fill in the required fields");
      return;
    }
    try {
      if (isEditing) {
        await updatePermission(token, formData.id, formData);
        notify.success("Updated successfully");
      } else {
        await createPermission(token, formData);
        notify.success("Created successfully");
      }
      resetForm();
      fetchPermissions();
    } catch (err) {
      notify.error("Something went wrong");
    }
  };

  const resetForm = () => {
    setFormData({ id: null, name: "", description: "", module: "" });
    setIsEditing(false);
    setErrors({});
  };

  const confirmDelete = async () => {
    try {
      setStatus('loading');
      await deletePermission(token, modalConfig.id);
      setStatus('success');
    
    // إغلاق المودال تلقائياً بعد ثانية ونصف من النجاح
    setTimeout(() => {
        setModalConfig({ isOpen: false, id: null });
        setStatus('idle');
    }, 1500);


      
      notify.success("Permission deleted successfully");
      fetchPermissions();
    } catch (err) {
      const serverData = err.data;
      setStatus('error'); // تفعيل أنيميشن الاهتزاز
   
      if (serverData?.groups && serverData.groups.length > 0) {
        notify.error(`Denied: Assigned to Groups [${serverData.groups.join(", ")}]`, { duration: 5000 });
      } else {
        notify.error(err.message || "Failed to delete permission");
      }
      setTimeout(() => {
        setModalConfig({ isOpen: false, id: null });
        setStatus('idle');
      }, 2000);
    } /*finally {
      setModalConfig({ isOpen: false, id: null });
    }*/
  };

  const sortedData = useMemo(() => {
    let sortableItems = [...permissions].filter(p => 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.module.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let valA = a[sortConfig.key] || "";
        let valB = b[sortConfig.key] || "";
        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [permissions, searchTerm, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage) || 1;
  const totalItems = Math.ceil(sortedData.length ) || 1;
  const currentData = sortedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // تعريف الأعمدة للجدول المركزي
  const columns = [
    { key: "id", label: "ID", className: "text-gray-400 font-mono" },
    { key: "name", label: "Name", className: "font-bold text-gray-700" },
    { 
      key: "module", 
      label: "Module", 
      render: (p) => (
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
          </span>
          <span className="text-sm font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-100">
            {p.module}
          </span>
        </div>
      )
    },
    { key: "description", label: "Description", className: "text-gray-500 italic" }
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <ConfirmModal 
        isOpen={modalConfig.isOpen}
        title="Delete Permission"
        message="Are you sure you want to remove this permission from the system?"
        onConfirm={confirmDelete}
        onCancel={() => setModalConfig({ isOpen: false, id: null })}
        isLoading={status === 'loading'}
  isSuccess={status === 'success'} 
  isError={status === 'error'}
      />
      
      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
        <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] shrink-0 text-[var(--primary-color)]">
            <ICONS.Lock size={40} />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>Permissions</h2>
            <p className="text-gray-400 text-xs md:text-sm">Manage access control levels</p>
          </div>
        </div>
        <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Search permissions..." />
      </PageHeader>

      {/* Form Section */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm mb-8">
        <h3 className="text-sm font-bold text-gray-400 uppercase mb-6 tracking-widest">
          {isEditing ? "Edit Permission" : "Create New Permission"}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          <FloatingInput
            label="Permission Name"
            error={errors.name}
            value={formData.name}
            onChange={(e) => {
              setFormData({...formData, name: e.target.value});
              if (errors.name) setErrors({...errors, name: ""});
            }}
          />
          <FloatingInput
            label="Module Segment"
            error={errors.module}
            value={formData.module}
            onChange={(e) => {
              setFormData({...formData, module: e.target.value});
              if (errors.module) setErrors({...errors, module: ""});
            }}
          />
          <FloatingInput
            label="Description (Optional)"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
          />
          <div className="flex gap-2">
            <Button variant="primary" icon={ICONS.Plus} onClick={handleSubmit} className="flex-grow">
              {isEditing ? "Update" : "Add Permission"}
            </Button>
            {isEditing && (
              <Button variant="secondary" onClick={resetForm} icon={ICONS.Close} />
            )}
          </div>
        </div>
      </div>

      {/* DataTable المركزي الموحد */}
      <DataTable 
        columns={columns}
        data={currentData}
        sortConfig={sortConfig}
        onSort={(key) => setSortConfig({ key, direction: sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc" })}
        renderActions={(p, isMobile) => (
          <>
            <Button 
              variant="outline" 
              size="sm" 
              icon={ICONS.EditUser} 
              onClick={() => { setFormData(p); setIsEditing(true); setErrors({}); }} 
            >
              {isMobile && " Edit"}
            </Button>
            <Button 
              variant="danger" 
              size="sm" 
              icon={ICONS.DeleteUser} 
              onClick={() => setModalConfig({ isOpen: true, id: p.id })} 
            >
              {isMobile && " Delete"}
            </Button>
          </>
        )}
        mobileRender={(p) => (
          <div className="flex flex-col gap-1">
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-blue-500"></span>
                <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest">{p.module}</span>
              </div>
              <span className="text-[10px] text-gray-400">#{p.id}</span>
            </div>
            <h4 className="font-bold text-gray-800">{p.name}</h4>
            <p className="text-xs text-gray-500">{p.description}</p>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: setCurrentPage,
          totalItems
        }}
      />
    </div>
  );
}